import { useGetSmartBillSyncStatus, useSearchContracts } from '@actions';
import { PaginationProps } from '@components/V4/Pagination';
import { SortOrder } from '@components/V4/Table/Table';
import Tabs from '@components/V4/Tabs/Tabs';
import { useDebounce, useQueryParamsState } from '@hooks';
import DetailsModal from '@modules/Admin/components/DetailsModal/DetailsModal';
import { contractsApis } from '@shared/apis';
import { ContractRowDTO } from '@shared/clientTypes/DTOs/ContractsDTO';
import useToast from 'apps/agora/src/hooks/useToast';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ContractDetailsModal from './components/ContractDetailsModal';
import ContractFormModal from './components/ContractFormModal';
import ContractImportModal from './components/ContractImportModal';
import ContractsHeader from './components/ContractsHeader';
import ContractsTable from './components/ContractsTable';
import PandaDocDocumentsTable from './components/PandaDocDocumentsTable';
import SyncStatusBar from './components/SyncStatusBar';
import UnmappedInvoicesTable from './components/UnmappedInvoicesTable';
import { contractsToCsv, downloadCsv } from './utils';

type ContractsTab = 'contracts' | 'unmapped' | 'documents';

const useQuery = () => new URLSearchParams(useLocation().search);

const Contracts = () => {
  const toast = useToast();
  const query = useQuery();

  const page = query.get('page') || '1';
  const limit = query.get('limit') || '10';
  const search = query.get('search') || null;
  const status = query.get('status') || null;
  const strategist = query.get('strategist') || null;
  const churnLevel = query.get('churnLevel') || null;
  const hasContract = query.get('hasContract') || null;
  const overdueOnly = query.get('overdueOnly') || 'unchecked';
  const showDeactivated = query.get('showDeactivated') || 'unchecked';
  const sortBy = query.get('sortBy') || null;
  const sortOrder = (query.get('sortOrder') || null) as SortOrder;

  const [searchParams, setSearchParams] = useQueryParamsState<any>({
    page,
    limit,
    search,
    status,
    strategist,
    churnLevel,
    hasContract,
    overdueOnly,
    showDeactivated,
    sortBy,
    sortOrder,
  });

  const [tab, setTab] = useState<ContractsTab>('contracts');
  const [searchValue, setSearchValue] = useState(search ?? '');
  const debouncedSearch = useDebounce(searchValue, 500);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<ContractRowDTO | null>(null);
  const [presetStudent, setPresetStudent] = useState<{ _id: string; fullName: string } | null>(
    null
  );
  const [detailsContractId, setDetailsContractId] = useState<string | null>(null);
  const [studentId, setStudentId] = useState<string>();

  useEffect(() => {
    if ((debouncedSearch || null) !== (searchParams.search || null)) {
      setSearchParams({ search: debouncedSearch || null, page: 1 });
    }
  }, [debouncedSearch]);

  const filters: contractsApis.SearchContracts['payload'] = {
    search: searchParams.search || undefined,
    status: searchParams.status || undefined,
    strategist: searchParams.strategist || undefined,
    churnLevel: searchParams.churnLevel || undefined,
    hasContract: searchParams.hasContract || undefined,
    overdueOnly: searchParams.overdueOnly || undefined,
    showDeactivated: searchParams.showDeactivated || undefined,
    sortBy: searchParams.sortBy || undefined,
    sortOrder: searchParams.sortOrder || undefined,
  };

  const { data, isLoading } = useSearchContracts({
    ...filters,
    page: searchParams.page,
    limit: searchParams.limit,
  });

  const exportQuery = useSearchContracts({ ...filters, page: 1, limit: 1000 }, { enabled: false });

  const { data: syncStatus } = useGetSmartBillSyncStatus({
    refetchInterval: (current) => (current?.running ? 10000 : 60000),
  });

  const exportHandler = async () => {
    const { data: exported } = await exportQuery.refetch();
    if (!exported?.contracts.length) {
      toast.error('Nothing to export for the current filters.');
      return;
    }
    downloadCsv(
      contractsToCsv(exported.contracts),
      `contracts-${moment().format('YYYY-MM-DD')}.csv`
    );
  };

  const pagination: PaginationProps = {
    pageSize: parseInt(searchParams.limit, 10) || 10,
    total: data?.total,
    currentPage: parseInt(searchParams.page, 10) || 1,
    onCurrentPageChange: (nextPage) => setSearchParams({ page: nextPage }),
    onPageSizeChange: (size) => setSearchParams({ limit: size, page: 1 }),
  };

  const openEdit = (contract: ContractRowDTO) => {
    setPresetStudent(null);
    setEditingContract(contract);
    setIsFormOpen(true);
  };

  const openCreate = (student?: { _id: string; fullName: string }) => {
    setEditingContract(null);
    setPresetStudent(student ?? null);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingContract(null);
    setPresetStudent(null);
  };

  const unmappedCount = syncStatus?.unmappedInvoices ?? 0;

  const renderContent = (currentTab: ContractsTab) => {
    if (currentTab === 'unmapped') {
      return <UnmappedInvoicesTable onOpenContract={setDetailsContractId} />;
    }

    if (currentTab === 'documents') {
      return <PandaDocDocumentsTable onOpenContract={setDetailsContractId} />;
    }

    return (
      <div className="flex flex-col gap-4">
        <ContractsHeader
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          strategists={data?.strategists}
          onNewContract={() => openCreate()}
          onImport={() => setIsImportOpen(true)}
          onExport={exportHandler}
          isExporting={exportQuery.isFetching}
        />
        <ContractsTable
          contracts={data?.contracts}
          isLoading={isLoading}
          pagination={pagination}
          sortBy={searchParams.sortBy}
          sortOrder={searchParams.sortOrder}
          setSearchParams={setSearchParams}
          onOpenContract={setDetailsContractId}
          onOpenStudent={setStudentId}
          onCreateContract={openCreate}
        />
      </div>
    );
  };

  return (
    <section className="min-h-[calc(100vh-74px)] bg-surfaceBackground p-6 font-raleway text-white tablet:p-8">
      <div className="mb-6 flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold">Contracts</h1>
          <p className="mt-1 text-sm text-customGrey">
            Every student, with contract, installment and collection data where it exists. The dot
            on each row is green once that contract's invoices are synced with SmartBill.
          </p>
        </div>
        <SyncStatusBar status={syncStatus} />
      </div>

      <Tabs
        tabs={[
          { label: 'Contracts', value: 'contracts' },
          {
            label: unmappedCount ? `Unmapped invoices (${unmappedCount})` : 'Unmapped invoices',
            value: 'unmapped',
          },
          { label: 'PandaDoc documents', value: 'documents' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        renderContent={renderContent}
      />

      <ContractFormModal
        isOpen={isFormOpen}
        contract={editingContract}
        presetStudent={presetStudent}
        onClose={closeForm}
      />

      <ContractImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onOpenContract={(contractId) => {
          setIsImportOpen(false);
          setDetailsContractId(contractId);
        }}
      />

      {detailsContractId ? (
        <ContractDetailsModal
          contractId={detailsContractId}
          isOpen={true}
          onClose={() => setDetailsContractId(null)}
          onEdit={openEdit}
          onOpenStudent={setStudentId}
        />
      ) : null}

      {studentId ? (
        <DetailsModal
          studentId={studentId}
          page="student"
          isOpen={true}
          onClose={() => setStudentId(undefined)}
        />
      ) : null}
    </section>
  );
};

export default Contracts;
